import type { Factura } from "../api/tipos.ts";

/**
 * Qué factura quiso decir quien etiqueta (mejora propuesta el 29-sep-2026).
 *
 * El caso real que lo motiva: Lorus etiquetó a mano pagos contra "1089",
 * "1114", "1166"… sin prefijo, porque así se dicen las facturas en voz alta.
 * En su ERP cada uno de esos números existe con 7 a 9 prefijos distintos
 * (FVEL, POSV, NCFE, FVE…), así que el conector, con razón, se negó a elegir y
 * los dejó sin asentar.
 *
 * El sitio para deshacer esa ambigüedad es AQUÍ, al etiquetar, con una persona
 * delante que puede confirmar "¿te refieres a FVEL1089?". Resolverlo después,
 * en el ERP y sin nadie mirando, sería elegir a ciegas con mejor puntería.
 *
 * Por eso esto SUGIERE y nunca reemplaza solo: quien etiqueta sabe algo que la
 * cartera no dice.
 */

/** El número sin prefijo ni ceros a la izquierda: "FVEL01089" → "1089", "FV-2189" → "2189". */
export function numeroDe(factura: string): string {
  const m = String(factura ?? "").match(/(\d+)\s*$/);
  return (m?.[1] ?? "").replace(/^0+(?=\d)/, "");
}

const igual = (a: string, b: string) =>
  a.replace(/[\s-]/g, "").toLowerCase() === b.replace(/[\s-]/g, "").toLowerCase();

export interface Candidata {
  numero: string;
  cliente: string;
  totalCentavos: number | null;
  /** Su total es exactamente el importe del pago. Es la pista más fuerte que hay. */
  coincideMonto: boolean;
}

/**
 * Facturas de la cartera que podrían ser la que se tecleó.
 *
 * - Si lo tecleado YA es una factura de la cartera, no hay nada que sugerir.
 * - Si comparte el número con una o más, se ofrecen todas. Primero la que cuadra
 *   con el importe del pago, pero **se ofrecen todas**: que cuadre el importe es
 *   una pista, no una prueba, y en ventas de mostrador hay muchos importes iguales.
 * - Sin dígitos no hay número que buscar.
 */
export function candidatasPara(
  tecleado: string,
  cartera: Factura[],
  montoCentavos?: number | null,
): Candidata[] {
  if (cartera.some((f) => igual(f.numero, tecleado))) return [];
  const numero = numeroDe(tecleado);
  if (!numero) return [];

  return cartera
    .filter((f) => numeroDe(f.numero) === numero)
    .map((f) => ({
      numero: f.numero,
      cliente: f.cliente ?? "",
      totalCentavos: f.totalCentavos,
      coincideMonto: montoCentavos != null && f.totalCentavos === montoCentavos,
    }))
    .sort((a, b) => Number(b.coincideMonto) - Number(a.coincideMonto) || a.numero.localeCompare(b.numero));
}

/**
 * ¿Esto, escrito donde va el CLIENTE, parece un número de factura?
 *
 * Un prefijo corto y dígitos: "1146", "FVEL1146", "FV-1146". Es el error de la
 * etiqueta 433 de Lorus, que puso "1146" como cliente y el nombre de la empresa
 * como factura. No es inocuo: el aprendizaje guarda ese pagador como un cliente
 * llamado "1146", y la siguiente vez lo propone así.
 *
 * "Ferretería 2000" o "3M" no lo disparan: un nombre con números sigue teniendo
 * palabras.
 */
export function pareceNumeroDeFactura(texto: string): boolean {
  return /^\s*[A-Za-z]{0,5}[-\s]?\d{2,}\s*$/.test(String(texto ?? ""));
}

/**
 * ¿Esto, escrito donde va la FACTURA, parece un nombre?
 *
 * Una factura siempre lleva dígitos. Sin ninguno, casi seguro es el nombre del
 * cliente en el campo equivocado.
 */
export function pareceNombre(texto: string): boolean {
  const t = String(texto ?? "").trim();
  return t.length > 0 && !/\d/.test(t);
}
